/* eslint-disable max-lines */
/* eslint-disable complexity */
import ToggleWeightCost from "@/components/toggleWeightCost/toggleWeightCost.vue";
import { REQUEST_STATE, STATUS_CODE } from "@/enums";
import { Line } from "vue-chartjs";
import { ApiService } from "@/services/Api";
import filterData from "@/utils.js";
import { formatNumberWithComma } from "@/utils";
import { createNamespacedHelpers } from "vuex";
import moment from "moment";
import { tooltipTitle, tooltipClick, tooltipHover } from "./tooltipMethods";
import store from "@/store";

const DECIMALS = 2,
	{ mapGetters: userGetters } = createNamespacedHelpers("user"),
	{
		mapGetters: filtersGetters,
		mapMutations: filtersMutations,
		mapActions: filtersActions,
	} = createNamespacedHelpers("filters");

export default {
	extends: Line,

	components: { ToggleWeightCost },

	data() {
		return {
			isAvoidableWasteGraphVisible: false,
			comparePeriod: "day_before",
			compareDates: "",
			graphData: [],
			data: {
				labels: [],
				datasets: [],
			},
			activeLabelSlotIndex: 0,
			datasets: [],
			datasetColors: [store.getters["branding/colors"][3], "#7D7D7D", store.getters["branding/colors"][4]],

			isCostToggleActive: false,
			state: REQUEST_STATE.LOADING,
			REQUEST_STATE,

			options: {
				tooltips: {
					callbacks: {
						title: this.tooltipTitle,
						label: (context) => {
							const formattedValue = formatNumberWithComma(context.yLabel, DECIMALS);
							return this.getTooltipLabel(formattedValue);
						},
					},
				},
				onClick: this.tooltipClick,
				onHover: this.tooltipHover,
				legend: { display: false },
				responsive: true,
				maintainAspectRatio: false,
				animation: {
					easing: "easeInOutQuad",
					duration: 520,
				},
				scales: {
					xAxes: [
						{
							display: true,
							gridLines: {
								color: "rgba(200, 200, 200, 0)",
								lineWidth: 1,
								drawBorder: false,
								display: false,
							},
							ticks: {
								fontColor: store.getters["branding/colors"][0],
								autoSkip: false,

								fontFamily: "Nunito Sans",
								fontSize: 11,
								padding: 10,
							},
						},
					],
					yAxes: [
						{
							gridLines: {
								color: "rgba(200, 200, 200, 0.3)",
								lineWidth: 1,
								drawBorder: false,
								padding: "20px",
							},
							ticks: {
								beginAtZero: true,
								fontColor: store.getters["branding/colors"][0],
								fontSize: 11,
								padding: 20,
								fontFamily: "Nunito Sans",
								stepSize: 0.1,
								maxTicksLimit: 10,
								callback: (value) => this.getChartYAxisLabel(value),
							},
						},
					],
				},
			},
		};
	},

	methods: {
		chartDatasetElementProperty,
		getChartYAxisLabel,
		getTooltipLabel,
		getData,
		initGraph,
		populateLabels,
		parseDataAndInitDataset,
		generateReferenceLineDataset,
		labelSlots,
		setComparePeriod,
		setCompareDates,
		tooltipTitle,
		tooltipClick,
		tooltipHover,
		...filtersMutations(["setActiveDate"]),
		...filtersActions(["selectPeriod"]),
	},

	computed: {
		...userGetters(["activeOutlet", "weightUnit", "weightFactor"]),

		...filtersGetters(["activePeriod", "activeCompareTo", "activeDate"]),
	},

	watch: {
		isCostToggleActive() {
			this.initGraph();
		},
	},

	mounted() {
		this.getData();

		this.$eventBus.$on("reloadComponentData", this.getData);
	},

	beforeDestroy() {
		this.$eventBus.$off("reloadComponentData", this.getData);
	},
};

function labelSlots() {
	return {
		week: [
			[
				this.$t("days.monday"),
				this.$t("days.tuesday"),
				this.$t("days.wednesday"),
				this.$t("days.thursday"),
				this.$t("days.friday"),
				this.$t("days.saturday"),
				this.$t("days.sunday"),
			],
		],
		day: [
			[
				"03:30",
				"04:30",
				"05:30",
				"06:30",
				"07:30",
				"08:30",
				"09:30",
				"10:30",
				"11:30",
				"12:30",
				"13:30",
				"14:30",
				"15:30",
				"16:30",
				"17:30",
				"18:30",
				"19:30",
				"20:30",
				"21:30",
				"22:30",
				"23:30",
				"00:30",
				"01:30",
				"02:30",
			],
		],
		month: [Array.from({ length: moment(this.activeDate).daysInMonth() }, (val, key) => key + 1)],
		year: [
			[
				this.$t("month.january"),
				this.$t("month.february"),
				this.$t("month.march"),
				this.$t("month.april"),
				this.$t("month.may"),
				this.$t("month.june"),
				this.$t("month.july"),
				this.$t("month.august"),
				this.$t("month.september"),
				this.$t("month.october"),
				this.$t("month.november"),
				this.$t("month.december"),
			],
		],
	};
}

function chartDatasetElementProperty() {
	return this.isCostToggleActive ? "cost" : "weight";
}

function getChartYAxisLabel(value) {
	let maxValue = 0;
	for (let index = 0; index < this.data.datasets.length; index++) {
		const datasetMax = Math.max(...this.data.datasets[index].data);
		if (datasetMax > maxValue) maxValue = datasetMax;
	}
	const decimalPlaces = maxValue < 5 && maxValue > 0 ? 1 : 0;

	if (this.isCostToggleActive) return `${this.activeOutlet.currency} ${formatNumberWithComma(value, decimalPlaces)}`;

	return `${formatNumberWithComma(value, decimalPlaces)} ${this.weightUnit}`;
}

function getTooltipLabel(value) {
	const valueType = this.isCostToggleActive ? this.$t("global.cost") : this.$t("global.weight");
	return valueType + ": " + value;
}

function setComparePeriod() {
	if (this.activePeriod === "month" || this.activeCompareTo === "base_month")
		this.comparePeriod = "first_month_measured";
	else this.comparePeriod = this.activeCompareTo;
}

function setCompareDates() {
	// eslint-disable-next-line object-curly-newline
	const data = filterData.returnFiltersState({}),
		startDate = data.dates.start_date_compare.replaceAll("/", "."),
		endDate = data.dates.end_date_compare.replaceAll("/", "."),
		endDateIncl = moment(endDate, "DD.MM.YYYY").subtract(1, "days").format("DD.MM.YYYY");

	// If no previous year to compare to, set to empty string
	if (startDate === "") this.compareDates = "";
	else {
		this.compareDates = ["day_before", "same_day_week_before"].includes(this.activeCompareTo)
			? startDate
			: startDate + " - " + endDateIncl;
	}
}

// eslint-disable-next-line complexity
function populateLabels() {
	const startDate = this.activeDate;
	this.data.labels = [];
	this.data.labels = this.labelSlots()[this.activePeriod][this.activeLabelSlotIndex];

	if (this.activePeriod === "day") {
		const date = moment(startDate).startOf(this.activePeriod);

		if (this.$utils.isClosed(date)) this.data.labels = [this.$t("global.closed")];

		return;
	}

	for (let index = 0; index < this.data.labels.length; index++) {
		if (this.activePeriod === "week") {
			const fullDayName = this.data.labels[index],
				date = moment(startDate).startOf("isoweek").add(index, "days");

			let label = "";

			if (this.$utils.isClosed(date)) label = this.$t("global.closed");
			else label = [fullDayName, date.format("DD.MM.YYYY")];

			this.data.labels[index] = label;
		} else if (this.activePeriod === "month") {
			let shortDayName = moment(startDate).startOf(this.activePeriod).add(index, "days").format("dd."),
				label = "";

			const date = moment(startDate).startOf(this.activePeriod).add(index, "days");

			shortDayName = this.$utils.replaceDoubleDotWithSingleDot(shortDayName);

			if (this.$utils.isClosed(date)) label = this.$t("global.closed");
			else label = `${shortDayName} ${date.format("DD/MM/YYYY")}`;

			this.data.labels[index] = label;
		} else if (this.activePeriod === "year")
			this.data.labels[index] = this.data.labels[index] + " " + moment(this.activeDate).year();
	}
}

// eslint-disable-next-line complexity
function parseDataAndInitDataset(data, index) {
	this.data.datasets = [];
	this.$forceUpdate();

	const parsedDataForGraph = new Array(this.data.labels.length).fill(null),
		kgModifier = 1000;
	let propName;

	switch (this.activePeriod) {
		case "day":
			propName = "hour";
			break;
		case "week":
		case "month":
			propName = "day";
			break;
		case "year":
			propName = "month";
			break;
	}

	Object.keys(data).forEach((key) => {
		// eslint-disable-next-line no-magic-numbers
		parsedDataForGraph[data[key][propName] - 1] =
			this.chartDatasetElementProperty() === "weight"
				? (data[key].weight * this.weightFactor) / kgModifier
				: data[key].cost;
	});

	this.datasets.push({
		lineTension: 0,
		data: parsedDataForGraph,
		fullData: parsedDataForGraph,
		backgroundColor: "rgba(0, 0, 0, 0.0)",
		borderColor: this.datasetColors[index],
		// eslint-disable-next-line no-magic-numbers
		borderDash: index === 1 ? [10, 10] : [],
		pointBackgroundColor: this.datasetColors[index],
		borderWidth: 2,
		pointRadius: 4,
		active: true,
		mainColor: this.datasetColors[index],
	});
}

function generateReferenceLineDataset() {
	const dataArray = this.datasets[1].data,
		avgOfValues = dataArray.reduce((sum, val) => sum + val, 0) / dataArray.filter((item) => item !== null).length,
		// eslint-disable-next-line no-magic-numbers
		referenceDataset = Array(this.data.labels.length).fill(avgOfValues);

	this.datasets.push({
		lineTension: 0,
		data: referenceDataset,
		backgroundColor: "rgba(0, 0, 0, 0.0)",
		borderColor: this.datasetColors[2],
		borderWidth: 2,
		pointRadius: 0,
		pointBorderColor: "rgba(0, 0, 0, 0.0)",
		pointHitRadius: 15,
		pointBackgroundColor: this.datasetColors[2],
		active: true,
	});
}

async function getData() {
	this.state = REQUEST_STATE.LOADING;
	this.graphData = [];
	this.setCompareDates();

	try {
		/*
		 * @ts-ignore
		 * If no previous year to compare to, set compare data to empty array
		 */
		const currentData = (await ApiService.getPerformanceGraphData()).data,
			compareData = this.compareDates === "" ? [] : (await ApiService.getPerformanceGraphData(true)).data;

		this.graphData.push(currentData);
		this.graphData.push(compareData);

		this.initGraph();

		this.state = REQUEST_STATE.SUCCESS;
	} catch (error) {
		if (error.response && error.response.status === STATUS_CODE.MISSING_DATA) this.state = REQUEST_STATE.MISSING_DATA;
	}
}

function initGraph() {
	this.data.datasets = [];
	this.datasets = [];
	this.setCompareDates();
	this.populateLabels();
	this.graphData.forEach((element, index) => {
		this.parseDataAndInitDataset(element, index);
	});
	this.generateReferenceLineDataset();

	for (let index = 0; index < this.datasets.length; index++) this.data.datasets.push(this.datasets[index]);

	this.renderChart(this.data, this.options);
}
